// The venue's clock, picked from the same IANA list the service will judge.
//
// THIS USED TO BE A TEXT BOX, and the comment on it said a dropdown would be a
// second, shorter list that disagreed the first time somebody needed a zone we
// did not think of. That is true of a guessed list. It is not true of
// `Intl.supportedValuesOf("timeZone")`: that is this process's ICU database,
// which is the same question `refuseZone` asks of Node before the row is
// written. UTC is added because the service accepts the bare name and ICU
// lists it only as Etc/UTC.
//
// The field still accepts a typed name. The list is a search, not a lock: a
// name ICU on this browser has not heard of can still be sent, and the service
// is the judge. A `<select>` of four hundred names without a filter is not a
// search, and a `<datalist>` puts every name into the tree on first paint,
// which this console's open-a-show check reads for invented numbers.

import { useId, useMemo, useState, type ReactNode } from "react";
import { Info } from "@nova/desk-ui/kit";

export type TimeZoneFieldProps = {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
};

export function TimeZoneField({ value, onChange, disabled }: TimeZoneFieldProps): ReactNode {
  const id = useId();
  const [open, setOpen] = useState(false);
  const zones = useMemo(listedTimeZones, []);
  const query = value.trim().toLowerCase();
  const matches =
    query === "" ? zones : zones.filter((zone) => zone.toLowerCase().includes(query));

  // THE HELP IS AN (i) BESIDE THE WORDS, as the October hand-off draws it, and
  // so this is no longer one wrapping <label>. The (i) is a button; inside a
  // label with no `for` it would come ahead of the input and become the
  // label's control, leaving the combobox unnamed. The words are a label of
  // their own, `for` the input, and the (i) sits next to them (kit.tsx's
  // FieldRow does the same and says the rest).
  return (
    <div className="field">
      <span className="label">
        <label htmlFor={id}>Venue time zone</label>
        <Info label="About Venue time zone">
          Search by city, for example Europe/London or America/Chicago. UTC is also accepted.
        </Info>
      </span>
      <div className="console-zone">
        <input
          id={id}
          className="input"
          role="combobox"
          aria-expanded={open ? "true" : "false"}
          aria-controls="console-zone-list"
          aria-autocomplete="list"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
        />
        {open && matches.length > 0 ? (
          <ul className="console-zone-list" id="console-zone-list" role="listbox">
            {matches.map((zone) => (
              <li key={zone} role="presentation">
                <button
                  type="button"
                  className={
                    zone === value ? "console-zone-option console-zone-option-here" : "console-zone-option"
                  }
                  role="option"
                  aria-selected={zone === value ? "true" : "false"}
                  // mousedown so the pick lands before the input's blur closes the list
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onChange(zone);
                    setOpen(false);
                  }}
                  disabled={disabled}
                >
                  {zone}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function listedTimeZones(): readonly string[] {
  const found = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const rest = found.filter((zone) => zone !== "UTC").sort((a, b) => a.localeCompare(b));
  return ["UTC", ...rest];
}
